/**
 * Formatos do cadastro de gestante, parceiro e gestação (Módulo 2).
 * Os mesmos schemas validam o formulário (feedback imediato) e a API (proteção real).
 * Endereço e identificação seguem o bloco comum das fichas SINAN (doc 01 §2.4).
 */
import { z } from 'zod';
import { idadeGestacional } from '../datas/gestacao';
import { cnsSchema, cpfSchema, somenteDigitos } from './documentos';

// ---------------------------------------------------------------- listas (rótulos para as telas)
export const RACAS_COR = [
  'branca',
  'preta',
  'amarela',
  'parda',
  'indigena',
  'nao_informado',
] as const;
export const ROTULO_RACA_COR: Record<(typeof RACAS_COR)[number], string> = {
  branca: 'Branca',
  preta: 'Preta',
  amarela: 'Amarela',
  parda: 'Parda',
  indigena: 'Indígena',
  nao_informado: 'Ignorado',
};

export const ZONAS = ['urbana', 'rural', 'periurbana', 'ignorado'] as const;
export const ROTULO_ZONA: Record<(typeof ZONAS)[number], string> = {
  urbana: 'Urbana',
  rural: 'Rural',
  periurbana: 'Periurbana',
  ignorado: 'Ignorado',
};

export const SEXOS = ['masculino', 'feminino', 'indeterminado', 'nao_informado'] as const;
export const ROTULO_SEXO: Record<(typeof SEXOS)[number], string> = {
  masculino: 'Masculino',
  feminino: 'Feminino',
  indeterminado: 'Indeterminado',
  nao_informado: 'Ignorado',
};

export const RESPOSTAS_SINAN = [
  'sim',
  'nao',
  'nao_realizado',
  'nao_se_aplica',
  'ignorado',
] as const;
export const ROTULO_RESPOSTA_SINAN: Record<(typeof RESPOSTAS_SINAN)[number], string> = {
  sim: 'Sim',
  nao: 'Não',
  nao_realizado: 'Não realizado',
  nao_se_aplica: 'Não se aplica',
  ignorado: 'Ignorado',
};

/** Escolaridade no código das fichas SINAN (item 15). */
export const ESCOLARIDADE_SINAN: { codigo: number; rotulo: string }[] = [
  { codigo: 0, rotulo: 'Analfabeto' },
  { codigo: 1, rotulo: '1ª a 4ª série incompleta do EF' },
  { codigo: 2, rotulo: '4ª série completa do EF' },
  { codigo: 3, rotulo: '5ª a 8ª série incompleta do EF' },
  { codigo: 4, rotulo: 'Ensino fundamental completo' },
  { codigo: 5, rotulo: 'Ensino médio incompleto' },
  { codigo: 6, rotulo: 'Ensino médio completo' },
  { codigo: 7, rotulo: 'Educação superior incompleta' },
  { codigo: 8, rotulo: 'Educação superior completa' },
  { codigo: 9, rotulo: 'Ignorado' },
  { codigo: 10, rotulo: 'Não se aplica' },
];

export const RISCOS_GESTACIONAIS = ['habitual', 'alto_risco', 'nao_informado'] as const;
export const ROTULO_RISCO: Record<(typeof RISCOS_GESTACIONAIS)[number], string> = {
  habitual: 'Risco habitual',
  alto_risco: 'Alto risco',
  nao_informado: 'Não informado',
};

export const STATUS_GESTACAO = [
  'em_pre_natal',
  'em_acompanhamento',
  'internada',
  'parto_realizado',
  'finalizada',
  'cancelada',
] as const;
export const ROTULO_STATUS_GESTACAO: Record<(typeof STATUS_GESTACAO)[number], string> = {
  em_pre_natal: 'Em pré-natal',
  em_acompanhamento: 'Em acompanhamento',
  internada: 'Internada',
  parto_realizado: 'Parto realizado',
  finalizada: 'Finalizada',
  cancelada: 'Cancelada',
};
/** Gestação "em andamento": só pode haver uma por paciente (migration 0014). */
export const STATUS_EM_ANDAMENTO = ['em_pre_natal', 'em_acompanhamento', 'internada'] as const;

// ---------------------------------------------------------------- datas
/** Data de hoje (AAAA-MM-DD) no horário de Brasília, independente do fuso do computador. */
export function hojeEmBrasilia(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(agora);
}

const dataIso = z.iso.date({ message: 'Informe uma data válida.' });

const dataPassada = (rotulo: string) =>
  dataIso.refine((d) => d <= hojeEmBrasilia(), { message: `${rotulo} não pode estar no futuro.` });

// ---------------------------------------------------------------- campos
const texto = (rotulo: string, max = 255) =>
  z
    .string()
    .trim()
    .min(1, { message: `Informe ${rotulo}.` })
    .max(max, { message: `Use no máximo ${max} caracteres.` });

/** Texto opcional: vazio vira null (o banco guarda "não informado" como NULL). */
const opcional = (max = 255) =>
  z
    .string()
    .trim()
    .max(max, { message: `Use no máximo ${max} caracteres.` })
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/** Campo opcional que, se preenchido, passa por outro schema (CPF, CNS…). */
const opcionalCom = <T extends z.ZodType>(schema: T) =>
  z.union([z.literal('').transform(() => null), z.null(), schema]).optional();

const digitos = (quantidade: number, rotulo: string) =>
  z
    .string()
    .trim()
    .transform(somenteDigitos)
    .refine((v) => v.length === quantidade, {
      message: `${rotulo} deve ter ${quantidade} dígitos.`,
    });

const codigoIbge = z.string().regex(/^\d{7}$/, { message: 'Escolha o município na lista.' });

/** Endereço e dados sociodemográficos no padrão SINAN (gestante e parceiro). */
const blocoSinan = {
  nomeSocial: opcional(),
  nomeMae: opcional(),
  racaCor: z.enum(RACAS_COR).optional(),
  escolaridadeSinan: z.number().int().min(0).max(10).nullable().optional(),
  ocupacaoCbo: opcionalCom(digitos(6, 'O código CBO')),
  telefone: opcional(30),
  celular: opcional(30),
  cep: opcionalCom(digitos(8, 'O CEP')),
  logradouro: opcional(),
  numero: opcional(20),
  complemento: opcional(120),
  bairro: opcional(120),
  pontoReferencia: opcional(),
  distrito: opcional(120),
  zona: z.enum(ZONAS).nullable().optional(),
  municipioResidenciaIbge: opcionalCom(codigoIbge),
};

// ---------------------------------------------------------------- gestante
export const gestanteSchema = z.object({
  nome: texto('o nome completo'),
  dataNascimento: dataPassada('A data de nascimento'),
  cpf: opcionalCom(cpfSchema),
  cns: opcionalCom(cnsSchema),
  email: z
    .email({ message: 'Informe um e-mail válido.' })
    .max(255)
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  /** Unidade de referência: define o município da gestante para o escopo do gestor. */
  unidadeSaudeId: z.uuid({ message: 'Escolha a unidade de referência.' }),
  ...blocoSinan,
});
export type DadosGestante = z.infer<typeof gestanteSchema>;

// ---------------------------------------------------------------- parceiro
export const parceiroSchema = z.object({
  nome: texto('o nome do parceiro'),
  dataNascimento: opcionalCom(dataPassada('A data de nascimento')),
  cpf: opcionalCom(cpfSchema),
  cns: opcionalCom(cnsSchema),
  sexo: z.enum(SEXOS).optional(),
  antecedenteSifilis: z.enum(RESPOSTAS_SINAN).nullable().optional(),
  antecedenteSifilisTratado: z.enum(RESPOSTAS_SINAN).nullable().optional(),
  observacoes: opcional(2000),
  ...blocoSinan,
});
export type DadosParceiro = z.infer<typeof parceiroSchema>;

// ---------------------------------------------------------------- gestação
/** IG máxima aceita na abertura da gestação (semanas). */
export const IG_MAXIMA_SEMANAS = 42;

export const gestacaoSchema = z
  .object({
    unidadeSaudeId: z.uuid({ message: 'Escolha a unidade de pré-natal.' }),
    dataUltimaMenstruacao: opcionalCom(dataPassada('A DUM')),
    /** Informada quando a DUM é desconhecida (ex.: pelo ultrassom). Com DUM, é calculada. */
    dataProvavelParto: opcionalCom(dataIso),
    dataInicioPreNatal: opcionalCom(dataPassada('O início do pré-natal')),
    numeroSisprenatal: opcional(20),
    risco: z.enum(RISCOS_GESTACIONAIS).optional(),
    gravidezPlanejada: z.boolean().nullable().optional(),
    gestacaoMultipla: z.boolean().optional(),
    observacoes: opcional(2000),
  })
  .refine(
    (g) =>
      !g.dataUltimaMenstruacao ||
      g.dataUltimaMenstruacao > hojeEmBrasilia() || // já recusada acima ("no futuro")
      idadeGestacional(g.dataUltimaMenstruacao, hojeEmBrasilia()).semanas <= IG_MAXIMA_SEMANAS,
    {
      path: ['dataUltimaMenstruacao'],
      message: `A DUM indica mais de ${IG_MAXIMA_SEMANAS} semanas de gestação. Confira a data.`,
    },
  )
  .refine((g) => Boolean(g.dataUltimaMenstruacao) || Boolean(g.dataProvavelParto), {
    path: ['dataUltimaMenstruacao'],
    message: 'Informe a DUM ou, se ela for desconhecida, a data provável do parto.',
  });
export type DadosGestacao = z.infer<typeof gestacaoSchema>;

// ---------------------------------------------------------------- inativação e busca
export const inativarSchema = z.object({
  motivo: z
    .string()
    .trim()
    .min(5, { message: 'Explique o motivo (ex.: "cadastro duplicado de …").' })
    .max(500),
});

export type CriterioBusca =
  | { tipo: 'cpf'; valor: string }
  | { tipo: 'cns'; valor: string }
  | { tipo: 'data'; valor: string }
  | { tipo: 'nome'; valor: string };

/** Mínimo de letras para buscar por nome (evita listar a rede inteira). */
export const BUSCA_NOME_MINIMO = 3;

/**
 * Descobre o que foi digitado no campo único de busca:
 * 11 dígitos → CPF · 15 dígitos → CNS · dd/mm/aaaa → data de nascimento · texto → parte do nome.
 * Devolve null quando não dá para buscar (curto demais ou data inexistente).
 */
export function interpretarBusca(termo: string): CriterioBusca | null {
  const limpo = termo.trim();
  const data = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(limpo);
  if (data) {
    const iso = `${data[3]}-${data[2]}-${data[1]}`;
    return dataIso.safeParse(iso).success ? { tipo: 'data', valor: iso } : null;
  }
  if (/^[\d.\-\s]+$/.test(limpo)) {
    const numeros = somenteDigitos(limpo);
    if (numeros.length === 11) return { tipo: 'cpf', valor: numeros };
    if (numeros.length === 15) return { tipo: 'cns', valor: numeros };
    return null;
  }
  return limpo.length >= BUSCA_NOME_MINIMO ? { tipo: 'nome', valor: limpo } : null;
}
